import Text from '@gym-atoms/Text/Text';
import Button, { ButtonSizes, ButtonVariants } from '@gym-atoms/Button/Button';
import Input from '@gym-atoms/Input/Input';
import {
  AccessCategoryMappingFormInputs,
  GymAccessCategoryType
} from '@gym-particles/types/models';
import { useTranslation } from 'react-i18next';
import Dropdown, { DropdownChangeEvent, SelectItem } from '@gym-atoms/Dropdown/Dropdown';
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router';
import { API } from 'aws-amplify';
import { getAccessCategories, getAllMembershipsForSiteLocation } from '@gym-graphql/queries';
import {
  AddSiteLocationAccessCategoryInput,
  LambdaResponse,
  MembershipInfo,
  SiteLocationAccessCategoryInput,
  SiteLocationAccessCategorySite
} from '@gym-src/API';
import { fetchAccessCategories } from '@gym-redux/slices/accessCategorySlice';
import { useAppDispatch, useAppSelector } from '@gym-redux/store';
import DialogBox, { DialogBoxVariants } from '@gym-atoms/Dialog/DialogBox';
import { getGymChainById } from '@gym-redux/slices/gymChainSlice';
import { addAccessCategory, modifyAccessCategory } from '@gym-graphql/mutations';
import Alert, { AlertType, createAlert } from '../../atoms/Alert/Alert';

const GymAccessCategoryMappingTemplate = (props: GymAccessCategoryMappingTemplateProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { chainId, gymId } = useParams<{ chainId: string; gymId: string }>();
  const [isInserted, setIsInserted] = useState(false);
  const [failedDialogVisible, setFailedDialogVisible] = useState(false);
  const userRole = useAppSelector((state) => state.user.userRole);
  const [schedulesOnly, setSchedulesOnly] = useState(false);
  const alertRef = useRef<AlertType>(null);

  const dispatch = useAppDispatch();
  const templateTypeChangeHandler = useCallback(
    (state) => {
      if (props.templateType) {
        props.templateType(state);
      }
    },
    [props.templateType]
  );
  const userId = useAppSelector((state) => state.user.userId);

  useEffect(() => {
    dispatch(getGymChainById(+chainId));
  }, [chainId]);

  const getTranslationKey = (e: string): string => {
    return props.type === 'new'
      ? 'ADD_NEW_GYM_ACCESS_CATEGORY.' + e
      : 'MODIFY_GYM_ACCESS_CATEGORY.' + e;
  };

  const accessCategoryDefaultValues: AccessCategoryMappingFormInputs = {
    arxCategoryId: props.accessCategoryMapping?.arxAccessCategoryId || '',
    accessCategoryId: props.accessCategoryMapping?.accessCategoryId || -1,
    membershipIds: props.accessCategoryMapping?.siteLocationMembershipId
      ? props.accessCategoryMapping.siteLocationMembershipId
      : -1
  };

  const [formValues, setFormValues] = useState(accessCategoryDefaultValues);
  const [membershipsDropDownItems, setMembershipsDropDownItems] = useState<Array<SelectItem>>([]);
  const [accessCategoriesDropDownItems, setAccessCategoriesDropDownItems] = useState<
    Array<SelectItem>
  >([]);
  const [membershipsItems, setMembershipsItems] = useState<Array<MembershipInfo>>([]);
  const [accessCategoriesItems, setAccessCategoriesItems] = useState<Array<GymAccessCategoryType>>(
    []
  );
  const [selectedMembershipsItems, setSelectedMembershipsItems] = useState<number[]>([]);
  const [selectedAccessCategory, setSelectedAccessCategory] = useState<number>(-1);
  const isAccessCategoriesLoading = useAppSelector(
    (state) => state.accessCategory.isAccessCategoriesLoading
  );
  const [defaultSelectedMemberships, setDefaultSelectedMemberships] = useState<
    Array<SiteLocationAccessCategorySite>
  >([]);
  const {
    register,
    handleSubmit,
    setValue,
    errors,
    reset,
    getValues
  } = useForm<AccessCategoryMappingFormInputs>({
    defaultValues: accessCategoryDefaultValues
  });

  const emptyValues: AccessCategoryMappingFormInputs = {
    membershipIds: -1,
    accessCategoryId: -1,
    arxCategoryId: ''
  };

  const inputFieldChangeHandler = (e: FormEvent<HTMLInputElement>) => {
    setValue(e.currentTarget.name as keyof AccessCategoryMappingFormInputs, e.currentTarget.value);
    setFormValues({ ...formValues, [e.currentTarget.name]: e.currentTarget.value });
  };

  const dropdownChangeHandler = (e: DropdownChangeEvent) => {
    console.log(e);
    setValue(e.target.name as keyof AccessCategoryMappingFormInputs, e.value);
    setFormValues({ ...formValues, [e.target.name]: e.value });
  };

  const membershipsDropdownHandler = async (e: DropdownChangeEvent) => {
    const removedMemberships = defaultSelectedMemberships
      .map((y: SiteLocationAccessCategorySite) => y.membershipId)
      .filter((x: number) => !e.value.includes(String(x)));

    let response;

    if (removedMemberships.length > 0) {
      if (selectedMembershipsItems && selectedMembershipsItems?.length > e.value.length) {
        //response = await checkRemovableMemberships(removedMemberships);
        response = true;
      } else {
        response = true;
      }
    } else {
      response = true;
    }
    if (response) {
      setValue('membershipIds', e.value);
      setFormValues({ ...formValues, membershipIds: e.value });
    } else {
      return;
    }
    setSelectedMembershipsItems(e.value);
    console.log(formValues);
  };

  useEffect(() => {
    register('accessCategoryId', {
      required: t(getTranslationKey('FORM_REQUIRED_FIELD_ERROR')) as string
    });
    register('membershipIds', {
      validate: (value) => {
        if (value === -1) {
          return false;
        } else if (Array.isArray(value) && value.length === 0) {
          return false;
        } else {
          return true;
        }
      }
    });
    register('arxCategoryId');
  }, [register]);

  const getGymChainAccessCategories = () => {
    try {
      const response = (API.graphql({
        query: getAccessCategories,
        variables: {
          siteLocationId: gymId
        }
      }) as Promise<{
        data: { getAccessCategories: { items: GymAccessCategoryType[] } };
      }>).then((e) => {
        const accessCategoriesDropDown: SelectItem[] = e.data.getAccessCategories.items.map(
          (accessCategory) => {
            return { label: accessCategory.name, value: accessCategory.id };
          }
        );
        console.log({ accessCategoriesDropDown });
        setAccessCategoriesItems(e.data.getAccessCategories.items);
        setAccessCategoriesDropDownItems(accessCategoriesDropDown);
        console.log(accessCategoryDefaultValues);
        setFormValues(accessCategoryDefaultValues);
      });
    } catch (error) {
      setFailedDialogVisible(true);
      console.log(error);
    }
  };

  const getGymMemberships = () => {
    try {
      console.log({ formValues });
      console.log(gymId);
      const response = (API.graphql({
        query: getAllMembershipsForSiteLocation,
        variables: {
          siteLocationId: gymId,
          filterMemberships: false
        }
      }) as Promise<{
        data: { getAllMembershipsForSiteLocation: MembershipInfo[] };
      }>).then((e) => {
        console.log(e);
        const membershipDropDownItems =
          e.data.getAllMembershipsForSiteLocation
            .map((data) => {
              return {
                label: `${data.siteLocationName} • ${data.membershipName} (${data.importedId}) • ${data.program} [${data.scheduleType}]`,
                value: data.siteLocationMembershipId || -1
              };
            })
            .filter((item) => item !== undefined) || [];
        console.log({ membershipDropDownItems });
        setMembershipsItems(e.data.getAllMembershipsForSiteLocation);
        setMembershipsDropDownItems(membershipDropDownItems);
      });
    } catch (error) {
      setFailedDialogVisible(true);
      console.log(error);
    }
  };

  useEffect(() => {
    getGymChainAccessCategories();
    getGymMemberships();
  }, []);

  const addAccessCategoryMappingData = async (
    formInputAccessCategory: AccessCategoryMappingFormInputs
  ) => {
    console.log({ formInputAccessCategory });
    console.log({ accessCategoriesItems });
    const selectedAccessCategory = accessCategoriesItems.filter(
      (selectedAccessCategory) =>
        selectedAccessCategory.id === +formInputAccessCategory.accessCategoryId
    );
    console.log({ selectedAccessCategory });

    const addAccessCategoryInput: AddSiteLocationAccessCategoryInput = {
      name: selectedAccessCategory[0].name.replace(/'/g, "''"),
      siteLocationMembershipIds: Array.isArray(formInputAccessCategory.membershipIds)
        ? formInputAccessCategory.membershipIds.map((id) => +id)
        : [formInputAccessCategory.membershipIds],
      gender: selectedAccessCategory[0].gender,
      age: selectedAccessCategory[0].age,
      accessCategoryId: selectedAccessCategory[0].id,
      arxAccessCategoryId: formInputAccessCategory.arxCategoryId.replace(/'/g, "''"),
      status: selectedAccessCategory[0].status,
      createdBy: userId,
      lastModifiedBy: userId,
      schedulesOnly: schedulesOnly,
      siteLocationMemberships: []
    };

    addAccessCategoryInput.siteLocationMemberships = addAccessCategoryInput.siteLocationMembershipIds.map(
      (id) => membershipsDropDownItems.find((m) => m.value === id)?.label || ''
    );
    console.log(addAccessCategoryInput);

    try {
      const result = await (API.graphql({
        query: addAccessCategory,
        variables: {
          input: addAccessCategoryInput
        }
      }) as Promise<{
        data: { addAccessCategory: LambdaResponse };
      }>);
      if (result.data.addAccessCategory.error) {
        throw result.data.addAccessCategory;
      }
      createAlert(alertRef, {
        content: t(getTranslationKey('ALERT_MESSAGE_SUCCESS')),
        severity: 'success'
      });
      setTimeout(
        () => history.push(`/gymChains/${chainId}/gyms/${gymId}/gymAccessCategories`),
        3000
      );
    } catch (error) {
      setFailedDialogVisible(true);
      console.log(error);
    }
  };

  const modifyAccessCategoryMappingData = async (
    formInputAccessCategory: AccessCategoryMappingFormInputs
  ) => {
    console.log({ formInputAccessCategory });
    console.log({ accessCategoriesItems });
    const selectedAccessCategory = accessCategoriesItems.filter(
      (selectedAccessCategory) =>
        selectedAccessCategory.id === +formInputAccessCategory.accessCategoryId
    );
    console.log({ selectedAccessCategory });
    console.log(props.accessCategoryMapping?.siteLocationAccessCategoryId);

    const modifyAccessCategoryInput: SiteLocationAccessCategoryInput = {
      id: props.accessCategoryMapping?.siteLocationAccessCategoryId,
      name: selectedAccessCategory[0].name.replace(/'/g, "''"),
      siteLocationMembershipId: formInputAccessCategory.membershipIds,
      gender: selectedAccessCategory[0].gender,
      age: selectedAccessCategory[0].age || -1,
      arxAccessCategoryId: formInputAccessCategory.arxCategoryId.replace(/'/g, "''"),
      status: props.accessCategoryMapping?.status,
      createdBy: props.accessCategoryMapping?.createdBy || -1,
      lastModifiedBy: userId,
      schedulesOnly: schedulesOnly
    };

    console.log({ modifyAccessCategoryInput });

    try {
      const result = await (API.graphql({
        query: modifyAccessCategory,
        variables: {
          input: modifyAccessCategoryInput
        }
      }) as Promise<{
        data: { modifyAccessCategory: LambdaResponse };
      }>);
      console.log({ result });
      if (result.data.modifyAccessCategory.error) {
        throw result.data.modifyAccessCategory;
      }
      createAlert(alertRef, {
        content: t(getTranslationKey('ALERT_MESSAGE_SUCCESS')),
        severity: 'success'
      });
      setTimeout(
        () => history.push(`/gymChains/${chainId}/gyms/${gymId}/gymAccessCategories`),
        3000
      );
    } catch (error) {
      setFailedDialogVisible(true);
      console.log(error);
    }
  };

  const onSubmitHandler = async (data: AccessCategoryMappingFormInputs) => {
    setIsInserted(true);
    if (props.type === 'new') {
      await addAccessCategoryMappingData(data);
    } else {
      await modifyAccessCategoryMappingData(data);
      templateTypeChangeHandler(false);
    }
    dispatch(
      fetchAccessCategories({
        siteLocationId: +gymId
      })
    );
    setIsInserted(false);
  };

  const ActionFailedDialog = () => {
    return (
      <div>
        <DialogBox
          variant={DialogBoxVariants.basic}
          dialogVisible={failedDialogVisible}
          onHideCallback={() => setFailedDialogVisible(false)}
          dialogHeader={t(getTranslationKey('FORM_FAILURE_DIALOG_HEADER'))}
          dialogFooter={
            <Button
              label={t(getTranslationKey('FORM_FAILURE_DIALOG_BUTTON'))}
              onClick={() => setFailedDialogVisible(false)}
            />
          }
          dialogDismissableMask={true}
          dialogClosable={false}
          content={<Text>{t(getTranslationKey('FORM_FAILURE_DIALOG_BODY'))}</Text>}
        />
      </div>
    );
  };

  return (
    <div className="ml30 mt10">
      <div className="d-flex flex-row">
        <div className="col">
          <Alert refProp={alertRef} />
        </div>
      </div>
      <div className="col-md-6">
        <form className="input-mb20 py-4" onSubmit={handleSubmit(onSubmitHandler)} noValidate>
          <div className="row mb25">
            <div className="col-12">
              <label className="fw-bold">
                {t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_ACCESS_CATEGORY_LABEL'))}
              </label>
              <Dropdown
                name="accessCategoryId"
                options={accessCategoriesDropDownItems}
                onChange={dropdownChangeHandler}
                value={formValues.accessCategoryId}
                //disabled={props.type === 'modify'}
                placeholder={t(
                  getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_ACCESS_CATEGORY_PLACEHOLDER')
                )}
              />
            </div>
          </div>
          <div className="row mb25">
            <div className="col-12">
              <label className="fw-bold">
                {t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_MEMBERSHIP_LABEL'))}
              </label>
              <Dropdown
                chips={false}
                name="membershipIds"
                options={membershipsDropDownItems}
                onChange={membershipsDropdownHandler}
                value={formValues.membershipIds}
                placeholder={t(
                  getTranslationKey(
                    `${
                      formValues.accessCategoryId === -1
                        ? 'FORM_INPUT_ACCESS_CATEGORY_MEMBERSHIP_PLACEHOLDER_DISABLED'
                        : 'FORM_INPUT_ACCESS_CATEGORY_MEMBERSHIP_PLACEHOLDER'
                    }`
                  )
                )}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <Input
                label={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_ARX_ID_LABEL'))}
                variant="basic"
                name="arxCategoryId"
                value={formValues.arxCategoryId}
                onChange={inputFieldChangeHandler}
                error={errors.arxCategoryId ? true : false}
                errorMessage={errors.arxCategoryId?.message}
                placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_ARX_ID_PLACEHOLDER'))}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="d-flex btn-min-w-110">
                <div className="mr15">
                  <Button
                    icon={isInserted ? 'pi-spinner pi-spin' : ''}
                    label={t(getTranslationKey('FORM_BTN_LABEL_SAVE'))}
                    size={ButtonSizes.medium}
                    disabled={isInserted}
                  />
                </div>
                <Button
                  label={t(getTranslationKey('FORM_BTN_LABEL_CANCEL'))}
                  variant={ButtonVariants.textonly}
                  onClick={() => {
                    history.push(`/gymChains/${chainId}/gyms/${gymId}/gymAccessCategories`);
                  }}
                  disabled={isInserted}
                />
              </div>
              <ActionFailedDialog />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

type GymAccessCategoryMappingTemplateType = 'new' | 'modify';

export interface GymAccessCategoryMappingTemplateProps {
  /** Type of the template: new or modify */
  type: GymAccessCategoryMappingTemplateType;
  /** Heading of the template */
  heading?: string;
  /** form values*/
  accessCategoryMapping?: GymAccessCategoryType | undefined;
  /** template change function */
  templateType?: (e: boolean) => void;
}

export default GymAccessCategoryMappingTemplate;
